/**
 * The /v1 contract (ARCHITECTURE.md §10). Request bodies are validated with
 * these schemas on the server; response types are inferred from them for
 * every client. Amounts are integer minor units with their currency.
 */
import { z } from "zod";
import { CROSSLIST_MARKETPLACES, JOB_ACTIONS, JOB_SOURCES, JOB_STATUSES, type CrossListing } from "@sable/connectors";
import {
  AUDIENCES, CONDITION_GRADES, COST_KINDS, ITEM_STATUSES, LISTING_STATUSES, PACKAGING, PERMISSIONS,
  PHOTO_CONTENT_TYPES, PHOTO_KINDS, WEIGHT_UNITS, PHOTO_MAX_BYTES, ROLES, SEASONS, SETTABLE_STATUSES, SLOT_KEYS,
  WRITE_OFF_REASONS,
} from "@sable/domain";

export const API_VERSION = "v1";

const uuid = z.uuid();
const minor = z.number().int().min(0).max(100_000_000_000);
const signedMinor = z.number().int().min(-100_000_000_000).max(100_000_000_000);
const pct = z.number().min(0).max(100);
const currency = z.string().regex(/^[A-Z]{3}$/);
const country = z.string().regex(/^[A-Z]{2}$/);
const isoDate = z.iso.date();
const isoDateTime = z.iso.datetime({ offset: true });
const text = (max: number) => z.string().trim().max(max);

// ---- Errors --------------------------------------------------------------

export const ApiErrorBody = z.object({
  error: z.string(),
  message: z.string(),
  detail: z.unknown().optional(),
});
export type ApiErrorBody = z.infer<typeof ApiErrorBody>;

// ---- Identity and workspaces ---------------------------------------------

export const WorkspaceSummary = z.object({
  id: uuid,
  name: z.string(),
  role: z.enum(ROLES),
  country,
  homeCurrency: currency,
});
export type WorkspaceSummary = z.infer<typeof WorkspaceSummary>;

export const Me = z.object({
  user: z.object({ id: uuid, email: z.string(), displayName: z.string().nullable() }),
  workspaces: z.array(WorkspaceSummary),
  cell: z.string(),
});
export type Me = z.infer<typeof Me>;

export const UNIT_SYSTEMS = ["imperial", "metric"] as const;

export const WorkspaceCreate = z.object({
  name: text(80).min(1),
  country,
  currency,
  timezone: z.string().min(1).max(64),
  units: z.enum(UNIT_SYSTEMS),
  locale: z.string().min(2).max(35).default("en-US"),
});
export type WorkspaceCreate = z.infer<typeof WorkspaceCreate>;

export const Workspace = z.object({
  id: uuid,
  name: z.string(),
  country,
  homeCurrency: currency,
  timezone: z.string(),
  units: z.enum(UNIT_SYSTEMS),
  locale: z.string(),
  skuPrefix: z.string(),
  skuPadding: z.number().int(),
  personalItems: z.boolean(),
  role: z.enum(ROLES),
  permissions: z.array(z.enum(PERMISSIONS)),
});
export type Workspace = z.infer<typeof Workspace>;

export const WorkspaceUpdate = z.object({
  name: text(80).min(1),
  timezone: z.string().min(1).max(64),
  units: z.enum(UNIT_SYSTEMS),
  locale: z.string().min(2).max(35),
  skuPrefix: z.string().regex(/^[A-Z0-9-]{0,8}$/),
  personalItems: z.boolean(),
}).partial();
export type WorkspaceUpdate = z.infer<typeof WorkspaceUpdate>;

export const Member = z.object({
  userId: uuid,
  email: z.string(),
  displayName: z.string().nullable(),
  role: z.enum(ROLES),
  status: z.enum(["active", "suspended"]),
});
export type Member = z.infer<typeof Member>;

// ---- Settings and fees ---------------------------------------------------

export const Settings = z.object({
  defaultPlatformFeePct: pct,
  paymentProcessingPct: pct,
  paymentProcessingFlatMinor: minor,
  avgPackagingCostMinor: minor,
  polyMailerCostMinor: minor,
  bubbleMailerCostMinor: minor,
  avgShippingCostMinor: minor,
  buyerPaysShipping: z.boolean(),
  mileageRate: z.number().min(0).max(100),
  targetMarginPct: pct,
  minAcceptableProfitMinor: minor,
});
export type Settings = z.infer<typeof Settings>;
export const SettingsUpdate = Settings.partial();
export type SettingsUpdate = z.infer<typeof SettingsUpdate>;

export const Marketplace = z.object({
  code: z.string(),
  name: z.string(),
  transport: z.enum(["api", "extension", "file", "manual"]),
});
export type Marketplace = z.infer<typeof Marketplace>;

export const MarketplaceFee = z.object({
  marketplace: z.string(),
  commissionPct: pct,
  fixedFeeMinor: minor,
  paymentPct: pct,
  paymentFixedMinor: minor,
  lowPriceThresholdMinor: minor.nullable(),
  lowPriceFeeMinor: minor.nullable(),
  sellerPaysShipping: z.boolean(),
  boostPct: pct,
});
export type MarketplaceFee = z.infer<typeof MarketplaceFee>;
export const MarketplaceFeeUpdate = MarketplaceFee.omit({ marketplace: true }).partial();
export type MarketplaceFeeUpdate = z.infer<typeof MarketplaceFeeUpdate>;

// ---- Items ---------------------------------------------------------------

export const Photo = z.object({
  id: uuid,
  kind: z.enum(PHOTO_KINDS),
  slot: z.string().nullable(),
  url: z.string(),
  thumbUrl: z.string(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  sortOrder: z.number(),
  isPrimary: z.boolean(),
  /** Kept for the seller: never posted to a marketplace, never the cover. */
  isPrivate: z.boolean(),
});
export type Photo = z.infer<typeof Photo>;

export const ItemSummary = z.object({
  id: uuid,
  sku: z.string(),
  title: z.string().nullable(),
  brand: z.string().nullable(),
  category: z.string().nullable(),
  sizeLabel: z.string().nullable(),
  conditionGrade: z.string().nullable(),
  status: z.enum(ITEM_STATUSES),
  enrichmentStatus: z.enum(["pending", "running", "complete", "partial", "failed"]),
  currency,
  purchasePriceMinor: minor.nullable(),
  listPriceMinor: minor.nullable(),
  salePriceMinor: minor.nullable(),
  marketplaces: z.array(z.string()),
  thumbUrl: z.string().nullable(),
  isPersonal: z.boolean(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ItemSummary = z.infer<typeof ItemSummary>;

export const ITEM_SORTS = ["newest", "oldest", "price_high", "price_low", "updated"] as const;

export const ItemListQuery = z.object({
  status: z.enum([...ITEM_STATUSES, "active", "all"]).default("active"),
  q: text(120).optional(),
  archived: z.enum(["exclude", "only", "include"]).default("exclude"),
  personal: z.enum(["exclude", "only", "include"]).default("include"),
  sort: z.enum(ITEM_SORTS).default("newest"),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ItemListQuery = z.infer<typeof ItemListQuery>;

export const ItemList = z.object({
  items: z.array(ItemSummary),
  nextCursor: z.string().nullable(),
  counts: z.record(z.string(), z.number()),
});
export type ItemList = z.infer<typeof ItemList>;

const itemFields = {
  title: text(280).nullable(),
  brand: text(120).nullable(),
  category: text(80).nullable(),
  subcategory: text(80).nullable(),
  audience: z.enum(AUDIENCES).nullable(),
  season: z.enum(SEASONS).nullable(),
  sizeLabel: text(40).nullable(),
  sizeSystem: text(40).nullable(),
  colorway: text(80).nullable(),
  materials: z.array(text(60)).max(12),
  styleKeywords: z.array(text(40)).max(20),
  conditionGrade: z.enum(CONDITION_GRADES).nullable(),
  conditionNotes: text(2000).nullable(),
  description: text(5000).nullable(),
  measurements: z.record(z.string().max(40), z.union([z.number(), z.string().max(40)])),
  tagPriceMinor: minor.nullable(),
  purchasePriceMinor: minor.nullable(),
  purchaseDate: isoDate.nullable(),
  storageBin: text(40).nullable(),
  /** Grams; the app shows ounces or grams by the workspace's units. */
  itemWeightGrams: z.number().int().min(0).max(1_000_000).nullable(),
  /** The unit the weight was entered in and is shown in. */
  itemWeightUnit: z.enum(WEIGHT_UNITS).nullable(),
  /** Packaging details: how it ships and the package's size. */
  packagingType: z.enum(["polybag", "box"]).nullable(),
  packageLength: z.number().min(0).max(10000).nullable(),
  packageWidth: z.number().min(0).max(10000).nullable(),
  packageHeight: z.number().min(0).max(10000).nullable(),
  packageDimUnit: z.enum(["in", "cm"]).nullable(),
  sourceName: text(120).nullable(),
  locationId: uuid.nullable(),
  isPersonal: z.boolean(),
};

export const ItemCreate = z.object(itemFields).partial().extend({
  status: z.enum(SETTABLE_STATUSES).default("in_stock"),
});
export type ItemCreate = z.infer<typeof ItemCreate>;

export const ItemUpdate = z.object(itemFields).partial().extend({
  status: z.enum(SETTABLE_STATUSES).optional(),
  archived: z.boolean().optional(),
});
export type ItemUpdate = z.infer<typeof ItemUpdate>;

export const WriteOff = z.object({ reason: z.enum(WRITE_OFF_REASONS), note: text(500).optional() });
export type WriteOff = z.infer<typeof WriteOff>;

export const Listing = z.object({
  id: uuid,
  marketplace: z.string(),
  status: z.enum(LISTING_STATUSES),
  currency,
  listPriceMinor: minor.nullable(),
  /** The marketplace's own id, known once posted by a worker or imported. */
  externalId: z.string().nullable(),
  externalUrl: z.string().nullable(),
  listedAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  lastSyncedAt: z.string().nullable(),
  syncError: z.string().nullable(),
  /** The storefront account it is listed under (migration 0012), once a post or a shop reading has shown it. */
  accountId: z.string().nullable(),
  accountHandle: z.string().nullable(),
  /** The paired browser that posted it, by name. */
  postedFrom: z.string().nullable(),
});
export type Listing = z.infer<typeof Listing>;

export const ListingUpsert = z.object({
  marketplace: z.string().min(1).max(40),
  listPriceMinor: minor.nullable().optional(),
  externalUrl: z.url().max(1000).nullable().optional(),
  status: z.enum(["active", "delisted", "ended"]).default("active"),
});
export type ListingUpsert = z.infer<typeof ListingUpsert>;

export const Sale = z.object({
  id: uuid,
  marketplace: z.string(),
  currency,
  salePriceMinor: minor,
  buyerShippingPaidMinor: minor,
  soldAt: z.string(),
  shippedAt: z.string().nullable(),
  externalOrderId: z.string().nullable(),
  packaging: z.enum(PACKAGING).nullable(),
  boosted: z.boolean(),
  returned: z.boolean(),
  trackingNumber: z.string().nullable(),
});
export type Sale = z.infer<typeof Sale>;

export const SaleCreate = z.object({
  marketplace: z.string().min(1).max(40),
  salePriceMinor: minor,
  buyerShippingPaidMinor: minor.default(0),
  soldAt: isoDateTime,
  externalOrderId: text(120).nullable().optional(),
  packaging: z.enum(PACKAGING).nullable().optional(),
  boosted: z.boolean().default(false),
  /** Postage the seller paid, recorded as a shipping_label cost on the sale. */
  shippingLabelMinor: minor.nullable().optional(),
});
export type SaleCreate = z.infer<typeof SaleCreate>;

export const SaleUpdate = z.object({
  shipped: z.boolean(),
  trackingNumber: text(80).nullable(),
  carrier: text(40).nullable(),
  returned: z.boolean(),
}).partial();
export type SaleUpdate = z.infer<typeof SaleUpdate>;

export const Cost = z.object({
  id: uuid,
  kind: z.enum(COST_KINDS),
  currency,
  amountMinor: signedMinor,
  note: z.string().nullable(),
  incurredOn: z.string(),
  saleId: uuid.nullable(),
  isEstimate: z.boolean(),
});
export type Cost = z.infer<typeof Cost>;

export const CostCreate = z.object({
  kind: z.enum(COST_KINDS),
  amountMinor: minor,
  note: text(500).nullable().optional(),
  incurredOn: isoDate.optional(),
});
export type CostCreate = z.infer<typeof CostCreate>;

export const MarketProfile = z.object({
  currency,
  suggestedLowMinor: minor.nullable(),
  suggestedHighMinor: minor.nullable(),
  suggestedListMinor: minor.nullable(),
  floorPriceMinor: minor.nullable(),
  medianSoldMinor: minor.nullable(),
  compCount: z.number(),
  soldCompCount: z.number(),
  bestMonths: z.array(z.number()).nullable(),
  bestMarketplace: z.string().nullable(),
  confidence: z.number(),
  rationale: z.string().nullable(),
  computedAt: z.string(),
});
export type MarketProfile = z.infer<typeof MarketProfile>;

export const Ledger = z.object({
  grossRevenueMinor: signedMinor,
  platformFeeMinor: signedMinor,
  processingFeeMinor: signedMinor,
  recordedCostsMinor: signedMinor,
  netProfitMinor: signedMinor.nullable(),
  daysToSale: z.number().nullable(),
  daysHeld: z.number().nullable(),
});
export type Ledger = z.infer<typeof Ledger>;

export const ItemDetail = z.object({
  id: uuid,
  sku: z.string(),
  status: z.enum(ITEM_STATUSES),
  enrichmentStatus: z.enum(["pending", "running", "complete", "partial", "failed"]),
  enrichmentError: z.string().nullable(),
  /** The last AI cataloguing run: when, with what, which fields it holds, and what it says to check. */
  ai: z.object({
    at: z.string().nullable(),
    model: z.string().nullable(),
    fields: z.array(z.string()),
    notes: z.string().nullable(),
  }).nullable(),
  currency,
  ...itemFields,
  sourceName: z.string().nullable(),
  archivedAt: z.string().nullable(),
  listedAt: z.string().nullable(),
  soldAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  photos: z.array(Photo),
  listings: z.array(Listing),
  sale: Sale.nullable(),
  costs: z.array(Cost),
  profile: MarketProfile.nullable(),
  ledger: Ledger,
});
export type ItemDetail = z.infer<typeof ItemDetail>;

// ---- Photos --------------------------------------------------------------

export const PhotoUploadRequest = z.object({
  slot: z.enum(SLOT_KEYS),
  contentType: z.enum(PHOTO_CONTENT_TYPES),
  bytes: z.number().int().positive().max(PHOTO_MAX_BYTES),
  thumbBytes: z.number().int().positive().max(PHOTO_MAX_BYTES),
  width: z.number().int().positive().max(10_000),
  height: z.number().int().positive().max(10_000),
  /** Defaults by kind: a price tag starts private. */
  isPrivate: z.boolean().optional(),
});
export type PhotoUploadRequest = z.infer<typeof PhotoUploadRequest>;

export const PhotoUploadTicket = z.object({
  photoId: uuid,
  uploadUrl: z.string(),
  thumbUploadUrl: z.string(),
  /** Headers the PUTs must carry exactly, or S3 rejects the signature. */
  headers: z.record(z.string(), z.string()),
});
export type PhotoUploadTicket = z.infer<typeof PhotoUploadTicket>;

export const PhotoUpdate = z.object({
  isPrimary: z.literal(true).optional(),
  sortOrder: z.number().int().min(0).max(99).optional(),
  slot: z.enum(SLOT_KEYS).optional(),
  isPrivate: z.boolean().optional(),
});
export type PhotoUpdate = z.infer<typeof PhotoUpdate>;

// ---- Finance -------------------------------------------------------------

export const FinanceQuery = z.object({
  from: isoDate,
  to: isoDate,
  includePersonal: z.enum(["true", "false"]).default("false"),
});
export type FinanceQuery = z.infer<typeof FinanceQuery>;

export const FinanceSummary = z.object({
  currency,
  from: z.string(),
  to: z.string(),
  unitsSold: z.number(),
  grossRevenueMinor: signedMinor,
  feesMinor: signedMinor,
  costOfGoodsMinor: signedMinor,
  otherCostsMinor: signedMinor,
  netProfitMinor: signedMinor,
  months: z.array(z.object({
    month: z.string(),
    unitsSold: z.number(),
    grossRevenueMinor: signedMinor,
    netProfitMinor: signedMinor,
  })),
  byMarketplace: z.array(z.object({
    marketplace: z.string(),
    unitsSold: z.number(),
    grossRevenueMinor: signedMinor,
    netProfitMinor: signedMinor,
  })),
  inventory: z.object({
    activeUnits: z.number(),
    listedUnits: z.number(),
    capitalAtRestMinor: signedMinor,
  }),
});
export type FinanceSummary = z.infer<typeof FinanceSummary>;

export const SaleRow = z.object({
  saleId: uuid,
  itemId: uuid,
  sku: z.string(),
  title: z.string().nullable(),
  marketplace: z.string(),
  soldAt: z.string(),
  shippedAt: z.string().nullable(),
  currency,
  salePriceMinor: minor,
  netProfitMinor: signedMinor.nullable(),
  thumbUrl: z.string().nullable(),
});
export type SaleRow = z.infer<typeof SaleRow>;

export const SaleListQuery = z.object({
  shipped: z.enum(["all", "pending", "shipped"]).default("all"),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type SaleListQuery = z.infer<typeof SaleListQuery>;

// ---- Crosslisting ----------------------------------------------------------

export const SyncJob = z.object({
  id: uuid,
  marketplace: z.string(),
  action: z.enum(JOB_ACTIONS),
  status: z.enum(JOB_STATUSES),
  source: z.enum(JOB_SOURCES),
  attempts: z.number(),
  lastError: z.string().nullable(),
  scheduledFor: z.string(),
  createdAt: z.string(),
  finishedAt: z.string().nullable(),
});
export type SyncJob = z.infer<typeof SyncJob>;

/** The storefront account a browser is signed in to: the storefront's own user id and the shop's name. */
export const StorefrontAccount = z.object({
  id: z.string().max(100).nullable(),
  handle: z.string().max(100).nullable(),
});
export type StorefrontAccount = z.infer<typeof StorefrontAccount>;

export const ExtensionDevice = z.object({
  id: uuid,
  name: z.string(),
  version: z.string().nullable(),
  online: z.boolean(),
  paused: z.boolean(),
  /** Marketplace → whether the browser was signed in there at its last check-in. */
  signedIn: z.record(z.string(), z.boolean()),
  /** Marketplace → the account it was signed in to at its last check-in, where the extension could read it. */
  accounts: z.record(z.string(), StorefrontAccount),
  lastSeenAt: z.string().nullable(),
  pairedBy: z.string().nullable(),
  createdAt: z.string(),
});
export type ExtensionDevice = z.infer<typeof ExtensionDevice>;

/**
 * Why a marketplace can or can't take a post right now. `not_built`,
 * `off_in_country`, `listed_by_hand` (live, but posted outside the app, so a
 * second post would duplicate it) and `blocked` refuse the request; the rest
 * queue it, and the job waits for a worker.
 */
export const CROSSLIST_READINESS = [
  "ready", "not_built", "off_in_country", "listed_by_hand", "blocked", "no_device", "not_signed_in", "connect_account",
] as const;
export type CrosslistReadiness = (typeof CROSSLIST_READINESS)[number];

export const CrosslistPreview = z.object({
  title: z.string(),
  description: z.string(),
  condition: z.string().nullable(),
  category: z.array(z.string()),
  colors: z.array(z.string()),
  photoCount: z.number(),
  warnings: z.array(z.string()),
  blockers: z.array(z.string()),
});
export type CrosslistPreview = z.infer<typeof CrosslistPreview>;

/** The listing as each storefront's own form lays it out (@sable/connectors postingForm). */
export const PostingFormField = z.object({
  label: z.string(),
  sent: z.string().nullable(),
  kind: z.enum(["photos", "text", "longtext", "select", "path", "chips", "money", "fixed"]),
  value: z.union([z.string(), z.array(z.string()), z.number()]).nullable(),
  code: z.string().optional(),
  state: z.enum(["ok", "empty", "matched_on_post", "not_sent"]),
  note: z.string().optional(),
});
export type PostingFormField = z.infer<typeof PostingFormField>;

export const PostingForm = z.object({
  marketplace: z.enum(CROSSLIST_MARKETPLACES),
  posting: z.enum(["built", "not_built"]),
  currency,
  photoLimit: z.number(),
  photoAspect: z.number().nullable(),
  sections: z.array(z.object({ title: z.string(), fields: z.array(PostingFormField) })),
});
export type PostingForm = z.infer<typeof PostingForm>;

export const CrosslistTarget = z.object({
  marketplace: z.enum(CROSSLIST_MARKETPLACES),
  worker: z.enum(["extension", "api"]),
  /** Job actions this marketplace's worker is built for. */
  actions: z.array(z.enum(JOB_ACTIONS)),
  readiness: z.enum(CROSSLIST_READINESS),
  /** The price a new post would use: this listing's, else the suggested list price. */
  priceMinor: minor.nullable(),
  listing: Listing.nullable(),
  preview: CrosslistPreview,
  /** Every field and value a post would carry, in the storefront's form order. */
  form: PostingForm,
});
export type CrosslistTarget = z.infer<typeof CrosslistTarget>;

export const CrosslistState = z.object({
  targets: z.array(CrosslistTarget),
  jobs: z.array(SyncJob),
  devices: z.array(ExtensionDevice),
});
export type CrosslistState = z.infer<typeof CrosslistState>;

export const CrosslistRequest = z.object({
  targets: z.array(z.object({
    marketplace: z.enum(CROSSLIST_MARKETPLACES),
    priceMinor: minor.min(1),
  })).min(1).max(CROSSLIST_MARKETPLACES.length),
});
export type CrosslistRequest = z.infer<typeof CrosslistRequest>;

/** Post a listing again after a failure, or take it down on the marketplace itself. */
export const ListingRemoteAction = z.object({ action: z.enum(["publish", "delist"]) });
export type ListingRemoteAction = z.infer<typeof ListingRemoteAction>;

export const DevicePairRequest = z.object({ name: text(80).min(1) });
export type DevicePairRequest = z.infer<typeof DevicePairRequest>;

/** The token is shown once, here; only its hash is stored. */
export const DevicePairing = z.object({ device: ExtensionDevice, token: z.string() });
export type DevicePairing = z.infer<typeof DevicePairing>;

// ---- Extension protocol (/api/{cell}/ext, device token) ---------------------

const marketplaceCode = z.string().min(1).max(40);

export const ExtHeartbeat = z.object({
  version: z.string().max(40),
  signedIn: z.record(marketplaceCode, z.boolean()).default({}),
  /** The account signed in on each marketplace, where the page shows it. */
  accounts: z.record(marketplaceCode, StorefrontAccount).default({}),
  /** The job actions this build of the extension can do, per marketplace. */
  actions: z.record(marketplaceCode, z.array(z.string().max(20)).max(10)).default({}),
  paused: z.boolean().default(false),
});
export type ExtHeartbeat = z.infer<typeof ExtHeartbeat>;

export const ExtHeartbeatReply = z.object({
  workspace: z.object({ id: uuid, name: z.string(), country }),
  device: z.object({ id: uuid, name: z.string() }),
  /** Marketplaces this workspace can crosslist to through a browser, with their storefront origin. */
  marketplaces: z.array(z.object({ code: z.string(), origin: z.string() })),
  /** Runnable jobs this browser could claim now, per marketplace. */
  pendingJobs: z.record(z.string(), z.number()),
  /** Marketplaces whose sold page is due a check for this workspace. */
  salesDue: z.array(z.string()),
  /** Marketplaces whose whole shop someone asked to have read ("Read my shops"). */
  shopReads: z.array(z.string()),
  pollSeconds: z.number(),
});
export type ExtHeartbeatReply = z.infer<typeof ExtHeartbeatReply>;

export const ExtClaimRequest = z.object({ marketplaces: z.array(marketplaceCode).max(10) });
export type ExtClaimRequest = z.infer<typeof ExtClaimRequest>;

export const ExtJob = z.object({
  id: uuid,
  marketplace: z.string(),
  action: z.enum(JOB_ACTIONS),
  attempts: z.number(),
  externalId: z.string().nullable(),
  externalUrl: z.string().nullable(),
  currency,
  /** The listing's price, in minor units and in the major units storefront forms take. */
  priceMinor: minor.nullable(),
  price: z.number().nullable(),
});
export type ExtJob = z.infer<typeof ExtJob>;

export const ExtClaimReply = z.object({
  job: ExtJob.nullable(),
  /** What to post, for a publish; null for a delist or reprice. */
  listing: z.custom<CrossListing>().nullable(),
});
export type ExtClaimReply = z.infer<typeof ExtClaimReply>;

export const ExtJobResult = z.object({
  ok: z.boolean(),
  externalId: z.string().max(200).nullish(),
  externalUrl: z.url().max(2000).nullish(),
  note: z.string().max(2000).nullish(),
  error: z.string().max(4000).nullish(),
  /** The storefront account the job ran under, where the page showed it. */
  account: StorefrontAccount.nullish(),
  /** The marketplace refused outright (validation): don't retry. */
  final: z.boolean().default(false),
});
export type ExtJobResult = z.infer<typeof ExtJobResult>;

/** A storefront's sold page, as the extension recorded it: the JSON responses the page loaded. */
export const ExtSalesCapture = z.object({
  marketplace: marketplaceCode,
  payload: z.object({
    pageUrl: z.string().max(4000),
    host: z.string().max(255),
    captures: z.array(z.object({ url: z.string().max(4000), body: z.unknown() })).max(500),
  }),
});
export type ExtSalesCapture = z.infer<typeof ExtSalesCapture>;

/** Take on a requested shop reading; null when another browser already has it. */
export const ExtShopStart = z.object({ marketplace: marketplaceCode });
export type ExtShopStart = z.infer<typeof ExtShopStart>;
export const ExtShopStartReply = z.object({ readId: uuid.nullable() });
export type ExtShopStartReply = z.infer<typeof ExtShopStartReply>;

/**
 * One stretch of a shop reading: the shop-list responses the page loaded
 * since the last batch. `final` closes the reading and applies it; `dropped`
 * says responses were lost (over the size cap), so the reading can't count as
 * complete; `error` ends it as failed.
 */
export const ExtShopBatch = z.object({
  payload: ExtSalesCapture.shape.payload,
  final: z.boolean().default(false),
  dropped: z.boolean().default(false),
  error: z.string().max(2000).nullish(),
});
export type ExtShopBatch = z.infer<typeof ExtShopBatch>;

export const ShopReadResult = z.object({
  /** Listings the reading saw, and how many of them are live. */
  seen: z.number(),
  active: z.number(),
  /** The storefront said how big the shop is and the reading saw all of it. */
  complete: z.boolean(),
  /** Listings tied to an item, by how: already ours, SKU in the text, exact title. */
  linked: z.object({ listing: z.number(), sku: z.number(), title: z.number() }),
  /** Listings of ours newly tied to a storefront id. */
  newlyLinked: z.number(),
  /** Price, link or status brought up to date. */
  updated: z.number(),
  /** Our live listings the shop no longer shows: ended. */
  ended: z.number(),
  /** Shown sold on the storefront while the item was in stock: taken down elsewhere, sale to record. */
  soldElsewhere: z.number(),
  /** Waiting for a person. */
  review: z.number(),
});
export type ShopReadResult = z.infer<typeof ShopReadResult>;

export const ExtShopBatchReply = z.object({
  /** Listings seen so far in this reading. */
  seen: z.number(),
  result: ShopReadResult.nullable(),
});
export type ExtShopBatchReply = z.infer<typeof ExtShopBatchReply>;

export const ExtSalesReply = z.object({
  /** Captures the server could read; 0 means the page changed or loaded nothing useful. */
  recognized: z.number(),
  salesRecorded: z.number(),
  /** Listings shown sold with no sale details: the item came down elsewhere, the sale waits for a person. */
  awaitingSale: z.number(),
  takenDown: z.number(),
  alreadyKnown: z.number(),
  unmatched: z.number(),
});
export type ExtSalesReply = z.infer<typeof ExtSalesReply>;

// ---- Sales seen on marketplaces that tie to no item --------------------------

export const UnmatchedSale = z.object({
  id: uuid,
  marketplace: z.string(),
  externalOrderId: z.string(),
  externalListingId: z.string().nullable(),
  title: z.string().nullable(),
  currency,
  priceMinor: minor,
  soldAt: z.string(),
  reason: z.enum(["no_match", "bundle", "written_off"]),
});
export type UnmatchedSale = z.infer<typeof UnmatchedSale>;

// ---- Reading the seller's shops ("Read my shops") ------------------------------

export const SHOP_READ_STATUSES = ["requested", "running", "done", "failed", "cancelled"] as const;

export const ShopRead = z.object({
  id: uuid,
  marketplace: z.string(),
  status: z.enum(SHOP_READ_STATUSES),
  requestedAt: z.string(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  /** Listings seen so far, while it runs. */
  seenSoFar: z.number(),
  result: ShopReadResult.nullable(),
  error: z.string().nullable(),
});
export type ShopRead = z.infer<typeof ShopRead>;

export const ShopItemRef = z.object({
  id: uuid,
  sku: z.string(),
  title: z.string().nullable(),
  status: z.enum(ITEM_STATUSES),
  thumbUrl: z.string().nullable(),
});
export type ShopItemRef = z.infer<typeof ShopItemRef>;

export const SHOP_REVIEW_REASONS = ["no_match", "duplicate", "item_gone"] as const;

/** A live storefront listing waiting for a person. */
export const SeenListingRow = z.object({
  marketplace: z.string(),
  externalId: z.string(),
  url: z.string().nullable(),
  title: z.string().nullable(),
  brand: z.string().nullable(),
  sizeLabel: z.string().nullable(),
  currency,
  priceMinor: minor.nullable(),
  photoUrl: z.string().nullable(),
  reason: z.enum(SHOP_REVIEW_REASONS),
  /** For a duplicate or an item that's gone: the item it tied to. */
  item: ShopItemRef.nullable(),
  suggestions: z.array(ShopItemRef.extend({ score: z.number() })),
  lastSeenAt: z.string(),
});
export type SeenListingRow = z.infer<typeof SeenListingRow>;

export const ShopsState = z.object({
  marketplaces: z.array(z.object({
    code: z.string(),
    /** The latest reading, finished or not. */
    latest: ShopRead.nullable(),
    /** Live listings of ours on this storefront tied to a storefront id, and all open ones. */
    linkedListings: z.number(),
    listings: z.number(),
    /** A paired browser can take listings down there. */
    canTakeDown: z.boolean(),
  })),
  /** Paired browsers, to say whether anything can carry a reading out now. */
  devices: z.array(ExtensionDevice),
  review: z.array(SeenListingRow),
});
export type ShopsState = z.infer<typeof ShopsState>;

export const ShopReadRequest = z.object({ marketplaces: z.array(z.string().min(1).max(40)).max(10).optional() });
export type ShopReadRequest = z.infer<typeof ShopReadRequest>;

/** Tie a waiting storefront listing to an item. */
export const SeenListingLink = z.object({ itemId: uuid });
export type SeenListingLink = z.infer<typeof SeenListingLink>;

/** The shop check: where the latest readings and our records disagree (reconcile.ts in @sable/connectors). */
export const SHOP_DISCREPANCY_KINDS = [
  "not_in_inventory", "sold_elsewhere", "item_gone", "sale_not_recorded", "not_marked_listed", "not_live_there", "price_differs",
] as const;

export const ShopDiscrepancy = z.object({
  kind: z.enum(SHOP_DISCREPANCY_KINDS),
  marketplace: z.string(),
  externalId: z.string().nullable(),
  /** What the storefront showed; null for a listing of ours the reading didn't see. */
  seen: z.object({
    url: z.string().nullable(),
    title: z.string().nullable(),
    photoUrl: z.string().nullable(),
    status: z.enum(["active", "sold", "inactive"]),
    currency,
    priceMinor: minor.nullable(),
  }).nullable(),
  /** Our listing there, if any. */
  listing: z.object({
    status: z.enum(LISTING_STATUSES), currency, priceMinor: minor.nullable(), url: z.string().nullable(), accountHandle: z.string().nullable(),
  }).nullable(),
  item: ShopItemRef.nullable(),
  soldOn: z.object({ marketplace: z.string(), url: z.string().nullable() }).nullable(),
});
export type ShopDiscrepancy = z.infer<typeof ShopDiscrepancy>;

export const ShopReport = z.object({
  /** Each readable storefront and the reading the check is based on. */
  marketplaces: z.array(z.object({
    code: z.string(),
    readAt: z.string().nullable(),
    complete: z.boolean(),
    seen: z.number(),
  })),
  rows: z.array(ShopDiscrepancy),
});
export type ShopReport = z.infer<typeof ShopReport>;

// ---- Text-message alerts (Settings → Notifications) ---------------------------

export const SmsSubscription = z.object({
  status: z.enum(["none", "pending", "verified", "opted_out"]),
  /** Masked, e.g. "(352) •••-0123". */
  phone: z.string().nullable(),
  consentAt: z.string().nullable(),
  verifiedAt: z.string().nullable(),
  /** While pending: when the code stops working, and when another can be sent. */
  codeExpiresAt: z.string().nullable(),
  resendAt: z.string().nullable(),
});
export type SmsSubscription = z.infer<typeof SmsSubscription>;

/** Opting in: the number, and the ticked box for the consent text of this version. */
export const SmsOptIn = z.object({
  phone: z.string().trim().min(7).max(30),
  consent: z.literal(true),
  consentVersion: z.string().max(20),
});
export type SmsOptIn = z.infer<typeof SmsOptIn>;

export const SmsVerify = z.object({ code: z.string().regex(/^\d{6}$/) });
export type SmsVerify = z.infer<typeof SmsVerify>;
